import { FormField } from "@babackapp/ui/components/form";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import { useState } from "react";
import { PanelContent } from "../../lib/panel/panel-content";
import { ReturnPlaceholdersHint } from "../../components/return-placeholders-hint";
import { useWorkflowsEditorConfig } from "../../context/workflows-editor-config-context";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useNodeFormPath } from "../../hooks/use-node-form-path";

function TagListInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
}) {
  const [inputValue, setInputValue] = useState("");

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
    if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-1.5 border rounded-md bg-background min-h-8">
      {value.map((tag, i) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-muted rounded border"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(i)}
            className="hover:text-destructive"
            aria-label={`Remove tag: ${tag}`}
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
      <input
        className="flex-1 min-w-[80px] text-sm outline-none bg-transparent"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={addTag}
        placeholder={value.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

export function PanelModifyOrderTags() {
  const { t } = useTranslation("trackoffice");
  const form = useWorkflowForm();
  // Same node, different target: tracking edits Baback's internal order
  // tags, return applies the tags to the Shopify order (V1 parity).
  const { domain } = useWorkflowsEditorConfig();

  const addPath = useNodeFormPath("config.add");
  const removePath = useNodeFormPath("config.remove");

  if (!addPath || !removePath) {
    return null;
  }

  return (
    <PanelContent
      title={t(
        "workflowsEditor.modifyOrderTags.panelTitle",
        "Modify Order Tags",
      )}
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {domain === "return"
            ? t(
                "workflowsEditor.modifyOrderTags.returnDescription",
                "Configure which tags to add or remove on the Shopify order linked to the return.",
              )
            : t(
                "workflowsEditor.modifyOrderTags.description",
                "Configure which internal tags to add or remove from the order.",
              )}
        </p>
        {domain === "return" && <ReturnPlaceholdersHint />}

        <div className="flex flex-col gap-1.5">
          <span className="font-medium text-xs text-muted-foreground">
            {t("workflowsEditor.modifyOrderTags.addLabel", "Tags to add")}
          </span>
          <FormField
            control={form.control}
            name={addPath}
            render={({ field }) => (
              <TagListInput
                value={Array.isArray(field.value) ? (field.value as string[]) : []}
                onChange={field.onChange}
                placeholder={t(
                  "workflowsEditor.modifyOrderTags.addPlaceholder",
                  "Type a tag and press Enter",
                )}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="font-medium text-xs text-muted-foreground">
            {t(
              "workflowsEditor.modifyOrderTags.removeLabel",
              "Tags to remove",
            )}
          </span>
          <FormField
            control={form.control}
            name={removePath}
            render={({ field }) => (
              <TagListInput
                value={Array.isArray(field.value) ? (field.value as string[]) : []}
                onChange={field.onChange}
                placeholder={t(
                  "workflowsEditor.modifyOrderTags.removePlaceholder",
                  "Type a tag and press Enter",
                )}
              />
            )}
          />
        </div>
      </div>
    </PanelContent>
  );
}
